import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'

const A4_WIDTH_MM = 210
const A4_HEIGHT_MM = 297

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

async function waitForImages(container: HTMLElement): Promise<void> {
  const images = Array.from(container.querySelectorAll('img'))
  await Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete && img.naturalHeight > 0) {
            resolve()
            return
          }
          img.onload = () => resolve()
          img.onerror = () => resolve()
        })
    )
  )
}

/** Inline remote images as data URLs so html2canvas can paint them. */
async function inlineImagesAsDataUrls(container: HTMLElement): Promise<void> {
  const images = Array.from(container.querySelectorAll('img'))
  for (const img of images) {
    const src = img.getAttribute('src')
    if (!src || src.startsWith('data:')) continue

    try {
      const response = await fetch(src)
      if (!response.ok) continue
      const blob = await response.blob()
      img.src = await blobToDataUrl(blob)
    } catch (error) {
      console.warn('Could not inline image for PDF export:', error)
    }
  }

  await waitForImages(container)
}

/**
 * Generates a PDF from the same A4 page elements used in the live preview.
 * Each `.lc-a4-page` root in the container becomes one PDF page.
 */
export async function generateLoadConfirmationPdf(
  container: HTMLElement
): Promise<Blob> {
  await inlineImagesAsDataUrls(container)
  await waitForImages(container)

  const pages = Array.from(
    container.querySelectorAll<HTMLElement>('.lc-a4-page')
  )

  if (pages.length === 0) {
    throw new Error('No document pages found for PDF generation.')
  }

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  })

  for (let i = 0; i < pages.length; i++) {
    const pageEl = pages[i]
    const canvas = await html2canvas(pageEl, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: '#ffffff',
      width: pageEl.offsetWidth,
      height: pageEl.offsetHeight,
    })

    const imgData = canvas.toDataURL('image/jpeg', 0.95)
    if (i > 0) pdf.addPage()
    pdf.addImage(imgData, 'JPEG', 0, 0, A4_WIDTH_MM, A4_HEIGHT_MM)
  }

  return pdf.output('blob')
}
